import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/store";
import AppTable, { ColumnConfig } from "../../../../../../components/AppTable/AppTable";
import { SurveyParticipantListItem } from "../../../types";
import { useTranslation } from "react-i18next";
import { Button, Col, Form, Row } from "react-bootstrap";
import ModalGlobal from "../../../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../../../hooks/useModal";
import AppAttachFileInput from "../../../../../../components/AppInput/AppAttachFileInput";
import { useEffect, useState } from "react";
import { deleteSurveyParticipants, downloadParticipantsTemplate, exportSurveyParticipantsCsv, importParticipantsFromCSV } from "../../../api/surveyAPI";
import { addParticipants, setSurveyForm } from "../../../../../../redux/surveySlice";
import { SectionPermissionTypeEnum } from "../../../../../../types/SectionPermissionTypesEnum";
import SpinerLoader from "../../../../../../components/SpinerLoader";
import { ApiErrorResponse } from "../../../../../../types/ApiErrorResponse";
import { useSurveyParticipants } from "../../../hooks/useSurveyParticipants";
import { useLocation } from "react-router-dom";
import { ParticipationStatusEnum } from "../../../../../../types/ParticipationStatusEnum";
import { useNavigationGuard } from "../../../../../../contexts/NavigationGuardContext";
import i18n from "../../../../../../i18n";



export default function SurveyParticipantsList() {
    const { t } = useTranslation(['common', 'survey', 'surveyAdmin', 'participant']);
    const dispatch = useDispatch();
    const { navigateWithGuard } = useNavigationGuard();
    const location = useLocation();
    
    const survey = useSelector((state: RootState) => state.surveyForm.data);
    const hasSurveyId = typeof survey.id === "number" && survey.id > 0;
    const { data: surveyParticipantsPage, isLoading: isParticipantsLoading, refetch: refetchSurveyParticipants } = useSurveyParticipants({
        surveyId: survey.id ?? 0,
        config: {
            enabled: hasSurveyId,
        }
    });
    const [ csvFile, setCSVFile ] = useState<File | null>(null);
    const [ csvResetTrigger, setCsvResetTrigger ] = useState(0);
    
    const csvColumnKeys = [
        "externalApp",
        "externalAppPatientId",
        "nhc",
        "name",
        "surname",
        "surname2",
        "birthDate",
        "birthCountry",
        "zipCode",
        "city",
        "province",
        "region",
        "country",
        "phoneNumber",
        "email",
        "externalAppCampaignId",
        "externalAppCampaignDescription",
        "externalAppSurveyId",
        "externalAppSurveyDescription",
        "eventDate",
        "medicalActTypeCode",
        "descriptionTypeMedicalAct",
        "externalCenterCode",
        "centerName",
        "externalMedicalServiceCode",
        "medicalServiceDescription",
        "unitCode",
        "unitDescription",
        "floor",
        "block"
    ];

    const supportedLanguages = ["es", "ca", "en"];

    const requiredColumns = csvColumnKeys.map(key => ({
        key,
        translations: supportedLanguages.map(lang =>
            i18n.getFixedT(lang)(
                `surveyAdmin:form.participants.csvHeaders.${key}`
            )
        )
    }));


    const csvColumns = csvColumnKeys.map(key =>
        t(`surveyAdmin:form.participants.csvHeaders.${key}`)
    );


    const columns: ColumnConfig<SurveyParticipantListItem>[] = [
        {
            header: t("surveyAdmin:participant.tableHeader.participationStatus"),
            key: "participationStatus",
            render: (item) => {
                const statusKey = Object.keys(ParticipationStatusEnum).find(
                    key => ParticipationStatusEnum[key as keyof typeof ParticipationStatusEnum] === item.participationStatus
                );
                return statusKey ? t(`surveyAdmin:participant.status.${statusKey}`) : item.participationStatus;
            } },
        { header: t("surveyAdmin:participant.tableHeader.name"), key: "name" },
        { header: t("surveyAdmin:participant.tableHeader.birthDate"), key: "birthDate" },
        { header: t("surveyAdmin:participant.tableHeader.zipCode"), key: "zipCode" },
        { header: t("surveyAdmin:participant.tableHeader.phone"), key: "phone" },
        { header: t("surveyAdmin:participant.tableHeader.email"), key: "email" }
    ];

    const {
        showModal: showImportModal,
        openModal: openImportModal,
        closeModal: closeImportModal,
        isLoading: isImportLoading,
        errorModal: importErrorModal,
        setErrorModal: setImportErrorModal,
        setLoading: setImportLoading
    } = useModal();
    const { showModal: showSuccessImportModal, openModal: openSuccessImportModal, closeModal: closeSuccessImportModal } = useModal();
    const {
        showModal: showDeleteModal,
        modalData: deleteModalData,
        openModal: openDeleteModal,
        closeModal: closeDeleteModal,
        isLoading: isDeleteLoading,
        setLoading: setDeleteLoading,
        currentItem: participantToDelete,
        errorModal: deleteErrorModal,
        setErrorModal: setDeleteErrorModal
    } = useModal<SurveyParticipantListItem>();
    const importErrors = importErrorModal?.errorModal ?? "";
    const deleteError = deleteErrorModal?.errorModal ?? "";
    const [ importedCount, setImportedCount ] = useState<number | null>(null);
    const [ totalRowsToImport, setTotalRowsToImport ] = useState<number | null>(null);
    const [ selectedParticipantToDelete, setSelectedParticipantToDelete ] = useState<SurveyParticipantListItem | null>(null);
    const [ showExportModal, setShowExportModal ] = useState<boolean>(false);
    const [ isExportLoading, setIsExportLoading ] = useState<boolean>(false);
    const [ exportError, setExportError ] = useState<string>("");

    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetchSurveyParticipants();
        }
    }, [location.state, refetchSurveyParticipants]);

    const setImportErrors = (message: string) => {
        setImportErrorModal({ errorModal: message });
    };

    const setDeleteError = (message: string) => {
        setDeleteErrorModal({ errorModal: message });
    };
    
    const confirmImportData = async () => {
        setImportErrors("");
        let isValid = true;
        if (!survey.campaignId) {
            setImportErrors(t("surveyAdmin:form.errors.campaignRequired"));
            isValid = false;
        }
        if (!csvFile) {
            setImportErrors(t("surveyAdmin:form.errors.csvRequired"));
            isValid = false;
        }

        if (isValid) {
            setImportedCount(null);
            setImportLoading(true);
            const formData = new FormData();
    
            formData.append('campaignId', survey.campaignId!.toString());
            formData.append('surveyId', survey.id!.toString());
            formData.append('importedFile', csvFile!);
    
            try {
                const importedParticipants: SurveyParticipantListItem[] =  await importParticipantsFromCSV(formData);
                dispatch(addParticipants({
                    participants: importedParticipants
                }));
                if (hasSurveyId) {
                    await refetchSurveyParticipants();
                }
                setImportedCount(importedParticipants.length);
                closeImportModal();
                openSuccessImportModal(null, '', '', 'info', '');
            } catch (error) {
                const fetchError = error as ApiErrorResponse;
                setImportErrors(fetchError.message ?? t("common:requestError.errorBadRequest"));
            } finally {
                setImportLoading(false);
            }
        }
    }

    const handleOpenDeleteParticipantModal = (participant: SurveyParticipantListItem) => {
        setDeleteError("");
        setSelectedParticipantToDelete(participant);
        openDeleteModal(
            participant,
            t("participant:modal.participantModalHeaderDelete1"),
            t("participant:modal.participantModalHeaderDeleteWithSurvey", {
                participantName: participant.name,
                surveyName: survey.surveyName ?? "-"
            }),
            "deleteWithoutInput",
            ""
        ); 
    }

    const handleCloseDeleteParticipantModal = () => {
        setSelectedParticipantToDelete(null);
        closeDeleteModal();
    }

    const deleteParticipant = async () => {
        const participantToDeleteCurrent = selectedParticipantToDelete ?? participantToDelete;
        if (!participantToDeleteCurrent) {
            setDeleteError(t("common:requestError.errorBadRequest"));
            return;
        }

        setDeleteError("");
        setDeleteLoading(true);

        try {
            if (hasSurveyId) { 
                await deleteSurveyParticipants(participantToDeleteCurrent.id);
                await refetchSurveyParticipants();
            } else {
                const updatedItems = (survey.participants.items ?? []).filter(
                    (item) =>
                        item.id !== participantToDeleteCurrent.id
                );

                dispatch(setSurveyForm({
                    participants: {
                        ...survey.participants,
                        items: updatedItems,
                        totalRecords: updatedItems.length,
                        totalPages: Math.ceil(updatedItems.length / (survey.participants.size || 1))
                    }
                }));
            }

            handleCloseDeleteParticipantModal();
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setDeleteError(
                fetchError?.message
                ?? t("common:requestError.errorBadRequest")
            );
        } finally {
            setDeleteLoading(false);
        }
    }
    
    const openImportParticipantsModal = () => {
        setCSVFile(null);
        setImportedCount(null);
        setTotalRowsToImport(null);
        closeSuccessImportModal();
        setImportErrors("");
        openImportModal(null, '', '', 'importData', '');
    }

    const exportDataCsv = async () => {
        if (!hasSurveyId || !survey.id) {
            return;
        }

        setExportError("");
        setIsExportLoading(true);

        try {
            await exportSurveyParticipantsCsv(survey.id);
            setShowExportModal(false);
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setExportError(
                fetchError?.message
                ?? (error instanceof Error ? error.message : t("common:requestError.errorBadRequest"))
            );
        } finally {
            setIsExportLoading(false);
        }
    }

    const handleAddParticipant = () => {
        const redirectTo = "/admin/participants/new-participants";
        const redirectState = {
            campaignId: survey.campaignId ?? undefined,
            campaignName: survey.campaignName ?? undefined,
            surveyId: survey.id ?? undefined,
            surveyName: survey.surveyName ?? undefined,
            fromSurveyList: true,
        };

        navigateWithGuard(redirectTo, { state: redirectState });
    };


    return (
        <>
            <Row xs="auto" className="align-items-center">
                <Col xs={12} className="d-flex justify-content-end flex-nowrap gap-2">
                    <Button variant="secondary">{t("surveyAdmin:form.participants.attachParticipants")}</Button>
                    <Button variant="secondary" onClick={openImportParticipantsModal}>{t("surveyAdmin:form.participants.importParticipants")}</Button>
                    <Button variant="secondary" onClick={handleAddParticipant}>{t("surveyAdmin:form.participants.addParticipants")}</Button>
                </Col>
            </Row>
            <ModalGlobal
                show={showDeleteModal}
                onClose = {handleCloseDeleteParticipantModal}
                onSave = {deleteParticipant}
                title={deleteModalData.title}
                contentType={deleteModalData.type}
                subtitle={deleteModalData.subtitle}
                subtitle2={deleteModalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={deleteError}
                isLoading={isDeleteLoading}
            />                
            
            <ModalGlobal
                show={showImportModal}
                onClose={closeImportModal}
                body = {
                    <>
                        {importErrors && (
                            <label className="text-danger">{importErrors}</label>
                        )}                        
                        {isImportLoading 
                            ? <SpinerLoader />
                            : <>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t("surveyAdmin:form.surveyNameField")}</Form.Label>
                                            <Form.Control
                                                value={survey.surveyName ?? ""}
                                                disabled
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t("surveyAdmin:form.surveyCampaignField")}</Form.Label>
                                            <Form.Control
                                                value={survey.campaignName ?? ""}
                                                disabled
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <AppAttachFileInput
                                            requiredColumns={requiredColumns}
                                            headerCountText="participant:modal.participantModalHeaderCount"
                                            totalRowsToImport={totalRowsToImport}
                                            onSelectFile={setCSVFile}
                                            onClear={() => {
                                                setCSVFile(null);
                                                setCsvResetTrigger((prev) => prev + 1);
                                            }}
                                            clearTrigger={csvResetTrigger}
                                            setTotalRowsToImport={setTotalRowsToImport}
                                            fileType={"csv"}
                                        />
                                    </Col>
                                </Row>
                            </>
                        }
                    </>
                }
                saveText={t("surveyAdmin:form.participants.attachParticipants")}
                closeText={t("common:buttons.buttonCancel")}
                error={importErrors}
                onSave={confirmImportData}
                isLoading={isImportLoading}
            />
            <ModalGlobal
                show={showSuccessImportModal}
                onClose={closeSuccessImportModal}
                body={<>
                    <Row>
                        <Col md={12}>
                            <label>{t("surveyAdmin:form.participants.importParticipantsSuccessMessage", { importedCount: importedCount, totalCount: totalRowsToImport })}</label>
                        </Col>
                    </Row>
                </>}
                closeText={t("common:buttons.buttonClose")}
                isLoading={false}
            />
            <ModalGlobal
                show={showExportModal}
                onClose={() => {
                    setShowExportModal(false);
                    setExportError("");
                }}
                title={t("common:pagination.linkExportCSV")}
                contentType={"exportCsv"}
                subtitle={t("participant:modal.participantModalHeaderExport1")}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={exportError}
                onSave={exportDataCsv}
                isLoading={isExportLoading}
            />
            <AppTable<SurveyParticipantListItem>
                data={hasSurveyId ? (surveyParticipantsPage?.items ?? []) : (survey.participants.items ?? [])}
                columns={columns}
                emptyMessage={t("participant:nonParticipants")}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={hasSurveyId ? (surveyParticipantsPage?.page ?? 1) : survey.participants.page}
                totalPages={hasSurveyId ? (surveyParticipantsPage?.totalPages ?? 0) : survey.participants.totalPages}
                pageSize={hasSurveyId ? (surveyParticipantsPage?.size ?? 10) : survey.participants.size}
                paginationType={'surveyParticipants'}
                sectionPermission={SectionPermissionTypeEnum.SURVEYS}
                onDownloadTemplate={() => downloadParticipantsTemplate(csvColumns, "import_participants_template.xlsx")}
                downloadTemplateText={t("surveyAdmin:form.participants.downloadTemplate")}
                onExportCsv={() => hasSurveyId && setShowExportModal(true)}                
                onDelete={handleOpenDeleteParticipantModal}
            />
            {isParticipantsLoading && hasSurveyId && <SpinerLoader />}
        </>
    );
}


